import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getOverview } from '../api';
import { colours as c, font, gbp } from '../theme';

const REGISTRATION_URL =
  process.env.REACT_APP_REGISTRATION_URL || window.location.origin + '/register';

export default function AdminQr() {
  const [pot, setPot] = useState(null);

  useEffect(() => {
    getOverview().then(res => setPot(res.data.pot)).catch(() => {});
  }, []);

  const downloadPng = () => {
    const svg = document.getElementById('qr-code');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const a = document.createElement('a');
      a.download = '100club-qrcode.png';
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Opens a print-ready A4 sheet in a new window.
  const printPoster = () => {
    const svg = document.getElementById('qr-code');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const potLine = pot
      ? `Last month's pot was ${gbp(pot.total)} — first prize ${gbp(pot.first)}.`
      : '';

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>100 Club poster</title>
<style>
  @page { size: A4; margin: 0; }
  body { margin:0; font-family: Georgia, serif; background:#f7f4ed; color:#1f2724;
         width:210mm; height:297mm; display:flex; flex-direction:column;
         align-items:center; justify-content:center; text-align:center; padding:24mm; box-sizing:border-box; }
  h1 { font-size:34pt; line-height:1.15; margin:0 0 6mm; font-weight:500; }
  p  { font-size:13pt; line-height:1.5; margin:0 0 4mm; color:#4e5652; }
  .qr { background:#fff; padding:8mm; border:1px solid #ddd7c9; margin:8mm 0; }
  .url { font-family: ui-monospace, monospace; font-size:11pt; color:#6c7470; }
  .brand { font-size:11pt; letter-spacing:0.14em; text-transform:uppercase; color:#6c7470; margin-bottom:8mm; }
</style></head><body>
  <div class="brand">Burwash Playing Fields Association</div>
  <h1>£3 a month.<br>Three winners every month.<br>All of it stays in the village.</h1>
  <p>${potLine}</p>
  <div class="qr">${svgData}</div>
  <p><strong>Scan to join the Burwash 100 Club</strong></p>
  <div class="url">${REGISTRATION_URL}</div>
  <script>window.onload = () => { window.print(); }</script>
</body></html>`);
    w.document.close();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <span style={S.intro}>
        For the pavilion door, the village hall and the summer fête
      </span>

      <div style={S.grid} className="grid-2">

        <div style={S.qrCard}>
          <div style={S.qrFrame}>
            <QRCodeSVG
              id="qr-code"
              value={REGISTRATION_URL}
              size={256}
              level="H"
              includeMargin
              bgColor="#ffffff"
              fgColor="#1f2724"
            />
          </div>
          <span style={S.url}>{REGISTRATION_URL}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
          <div style={S.card}>
            <span style={S.cardTitle}>Put the club where people already stand</span>
            <p style={S.body}>
              Anyone with a phone camera can join in about a minute — no app, no paper
              form for you to key in later. Print it big for the fête and small for the
              pavilion noticeboard.
            </p>
            <div style={S.actions}>
              <button style={S.primaryBtn} onClick={printPoster}>Print A4 poster</button>
              <button style={S.secondaryBtn} onClick={downloadPng}>Download QR only (PNG)</button>
            </div>
          </div>

          <div style={S.copyCard}>
            <span style={S.eyebrow}>Poster copy</span>
            <span style={S.copyHead}>
              £3 a month. Three winners every month. All of it stays in the village.
            </span>
            {pot && (
              <span style={S.copySub}>
                This month's pot is {gbp(pot.total)} — first prize {gbp(pot.first)}.
                Scan to join the Burwash 100 Club.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  intro: { fontSize: 14, color: c.muted },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 26, alignContent: 'start',
  },
  qrCard: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: 30, display: 'flex', flexDirection: 'column', gap: 20,
    alignItems: 'center', minWidth: 0,
  },
  qrFrame: {
    background: '#fff', border: `1px solid ${c.hairline}`, padding: 20,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  url: {
    fontSize: 13, color: c.muted, fontFamily: 'ui-monospace, monospace',
    wordBreak: 'break-all', textAlign: 'center',
  },

  card: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: '26px 28px', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0,
  },
  cardTitle: { fontFamily: font.serif, fontSize: 24, color: c.ink, lineHeight: 1.2 },
  body: { margin: 0, fontSize: 15, lineHeight: 1.6, color: c.body },
  actions: { display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' },
  primaryBtn: {
    padding: '13px 20px', border: `1px solid ${c.ink}`, background: c.ink,
    color: c.onTeal, borderRadius: 3, fontFamily: font.sans, fontSize: 14,
    fontWeight: 600, cursor: 'pointer', minHeight: 44,
  },
  secondaryBtn: {
    padding: '13px 20px', border: '1px solid #c9c2b1', background: c.card,
    color: c.ink, borderRadius: 3, fontFamily: font.sans, fontSize: 14,
    cursor: 'pointer', minHeight: 44,
  },

  copyCard: {
    background: c.teal, borderRadius: 3, padding: '26px 28px',
    display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
  },
  eyebrow: {
    fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: c.onTealDim,
  },
  copyHead: {
    fontFamily: font.serif, fontSize: 'clamp(21px, 2.6vw, 26px)',
    color: c.onTeal, lineHeight: 1.2,
  },
  copySub: { fontSize: 14, color: '#cfdcd6', lineHeight: 1.55 },
};